/*
 * gets the position of a given dom element in relation ot the viewport
 */
import debounce from 'lodash/debounce';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import { useViewportDom } from 'craft/state/viewport/selectors';
import { getOffsetAndStylesFromParent, isDomElement, OffsetsAndStyles } from 'craft/utils/viewport';

export const useOffsetFromViewport = (
  dom: HTMLElement | null,
  refToHide?: MutableRefObject<HTMLDivElement | null>,
) => {
  const { viewportContainerEl, viewportWrapperEl, scrollableBodyEl } = useViewportDom();

  const getOffsets = useCallback(() => getOffsetAndStylesFromParent(dom, viewportContainerEl), [
    dom,
    viewportContainerEl,
  ]);

  const [offsets, setOffsets] = useState<
    OffsetsAndStyles & { scrollTop: number; scrollLeft: number }
  >({
    ...getOffsets(),
    scrollTop: 0,
    scrollLeft: 0,
  });

  const latestOffsets = useRef(getOffsets());

  const setRefVisibility = (visible?: boolean) => {
    if (refToHide?.current) {
      // eslint-disable-next-line no-param-reassign
      refToHide.current.style.opacity = visible ? '1' : '0';
    }
  };

  const setOffsetsWithScroll = (offsetValues: OffsetsAndStyles) => {
    setOffsets({
      ...offsetValues,
      scrollTop: viewportWrapperEl?.scrollTop || 0,
      scrollLeft: viewportWrapperEl?.scrollLeft || 0,
    });
  };

  const debouncedSetOffsets = useCallback(
    debounce((value) => {
      setOffsetsWithScroll(value);
      setRefVisibility(true);
    }, 400),
    [],
  );

  const onScroll = () => {
    setRefVisibility(false);
    debouncedSetOffsets(getOffsets());
  };

  // we listen to the dom and to the viewport to see if there is any changes
  useEffect(() => {
    if (isDomElement(viewportWrapperEl)) {
      viewportWrapperEl.addEventListener('scroll', onScroll);
    }
    if (isDomElement(scrollableBodyEl)) {
      scrollableBodyEl.addEventListener('scroll', onScroll);
    }

    let resizeTimeout: any;
    let mutationTimeout: any;
    let positionInterval: any;

    const resizeObserver = new ResizeObserver(() => {
      resizeTimeout = setTimeout(() => {
        setOffsetsWithScroll(getOffsets());
      }, 10);
    });
    const mutationObserver = new MutationObserver(() => {
      mutationTimeout = setTimeout(() => {
        setOffsetsWithScroll(getOffsets());
      }, 10);
    });

    if (isDomElement(dom)) {
      resizeObserver.observe(dom);

      mutationObserver.observe(dom, {
        attributes: true,
        attributeFilter: ['style', 'data-refresh'],
      });

      positionInterval = setInterval(() => {
        const newOffsets = getOffsets();

        if (
          newOffsets.top === latestOffsets.current.top &&
          newOffsets.left === latestOffsets.current.left
        ) {
          return;
        }

        latestOffsets.current = newOffsets;
        setRefVisibility(false);
        debouncedSetOffsets(newOffsets);
      }, 20);
    }

    if (isDomElement(viewportContainerEl)) {
      resizeObserver.observe(viewportContainerEl);
      mutationObserver.observe(viewportContainerEl, {
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      if (isDomElement(viewportWrapperEl)) {
        viewportWrapperEl.removeEventListener('scroll', onScroll);
      }
      if (isDomElement(scrollableBodyEl)) {
        scrollableBodyEl.removeEventListener('scroll', onScroll);
      }
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (mutationTimeout) {
        clearTimeout(mutationTimeout);
      }
      if (positionInterval) {
        clearInterval(positionInterval);
      }
    };
  }, [dom]);

  return offsets;
};
